import { useState, useEffect } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    DialogActions,
    DialogContent,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import DialogHeader from "../../../components/Dialog/DialogHeader";
import UserDialog from "../../../components/Dialog/UserDialog";
import { Api } from "../../../request/api/api";
import { XpHistory } from "../../../model/Dto/XpHistory";

interface SourceOption {
    id: string;
    label: string;
    custom: boolean;
}

const OTHER_SOURCE: SourceOption = { id: "other", label: "Другое", custom: true };

const getTodayString = (): string => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const formatDate = (isoStr: string): string => {
    if (!isoStr) return "—";
    const part = isoStr.split("T")[0];
    const [y, m, d] = part.split("-");
    return `${d}.${m}.${y}`;
};

export interface AddExperienceDialogProps {
    fighterId: string;
    currentXp: number;
    canEdit: boolean;
    open: boolean;
    fetchData: () => void;
    onClose: () => void;
}

export default function AddExperienceDialog({ fighterId, currentXp, canEdit, open, fetchData, onClose }: AddExperienceDialogProps) {
    const [tab, setTab] = useState(0);
    const [amount, setAmount] = useState("1");
    const [sources, setSources] = useState<SourceOption[]>([]);
    const [sourceId, setSourceId] = useState("");
    const [customSource, setCustomSource] = useState("");
    const [date, setDate] = useState(getTodayString());
    const [history, setHistory] = useState<XpHistory[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const totalXp = history.reduce((sum, item) => item.xp > 0 ? sum + item.xp : sum, 0);
    const selectedSource = sources.find(s => s.id === sourceId);

    const loadSources = async () => {
        try {
            const data = await Api.dict.getAllXpSources();
            const opts: SourceOption[] = [
                ...data.map(s => ({ id: s.id, label: s.name, custom: false })),
                OTHER_SOURCE,
            ];
            setSources(opts);
            setSourceId(opts[0]?.id ?? OTHER_SOURCE.id);
        } catch {
            setSources([OTHER_SOURCE]);
            setSourceId(OTHER_SOURCE.id);
        }
    };

    const loadHistory = async () => {
        setLoading(true);
        try {
            const res = await Api.fighter.getFighterXpHistory(fighterId);
            if (res.success) setHistory(res.xpHistory);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            setTab(canEdit ? 0 : 1);
            setAmount("1");
            setCustomSource("");
            setDate(getTodayString());
            setError(null);
            setSuccess(false);
            setSources([]);
            setSourceId("");
            setHistory([]);
            loadSources();
            loadHistory();
        }
    }, [open, fighterId]);

    const getDescription = (): string => {
        if (selectedSource?.custom) return customSource.trim();
        return selectedSource?.label ?? "";
    };

    const validate = (): string | null => {
        const num = parseInt(amount);
        if (!num || num <= 0) return "Введите количество опыта больше 0";
        if (selectedSource?.custom && !customSource.trim()) return "Укажите источник опыта";
        if (!date) return "Выберите дату";
        if (date > getTodayString()) return "Дата не может быть в будущем";
        return null;
    };

    const handleAdd = async () => {
        const err = validate();
        if (err) { setError(err); return; }
        setLoading(true);
        setError(null);
        try {
            const ok = await Api.fighter.addFighterXp(fighterId, {
                xp: parseInt(amount),
                description: getDescription(),
                date,
            });
            if (ok) {
                setSuccess(true);
                setAmount("1");
                setCustomSource("");
                setDate(getTodayString());
                setError(null);
                await loadHistory();
                setTab(1);
                fetchData();
                setTimeout(() => setSuccess(false), 3000);
            } else {
                setError("Ошибка при добавлении опыта");
            }
        } catch {
            setError("Ошибка при добавлении опыта");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteXp = async (eventId: string) => {
        setLoading(true);
        try {
            const ok = await Api.fighter.deleteFighterXp(fighterId, eventId);
            if (ok) {
                await loadHistory();
                fetchData();
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    // Группировка по дате (часть "YYYY-MM-DD")
    const grouped = history
        .slice()
        .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
        .reduce<{ key: string; label: string; items: XpHistory[] }[]>((acc, item) => {
            const key = item.date ? item.date.split("T")[0] : "";
            const label = key ? formatDate(key) : "Без даты";
            const group = acc.find(g => g.key === key);
            if (group) group.items.push(item);
            else acc.push({ key, label, items: [item] });
            return acc;
        }, []);

    return (
        <UserDialog handleClose={handleClose} open={open}>
            <DialogHeader loading={loading} title="Опыт бойца" />
            <DialogContent sx={{ minHeight: 500, minWidth: 600, p: 0 }}>

                {/* Статистика */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack direction="row" spacing={3} justifyContent="space-between">
                                <Box sx={{ textAlign: "center", flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Всего получено
                                    </Typography>
                                    <Typography variant="h5" color="primary">{totalXp} XP</Typography>
                                </Box>
                                <Box sx={{ textAlign: "center", flex: 1, borderLeft: 1, borderRight: 1, borderColor: "divider", px: 2 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Доступно сейчас
                                    </Typography>
                                    <Typography variant="h5" color="success.main">{currentXp} XP</Typography>
                                </Box>
                                <Box sx={{ textAlign: "center", flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Записей в истории
                                    </Typography>
                                    <Typography variant="h5">{history.length}</Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>

                {/* Вкладки */}
                <Box sx={{ px: 2 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                        {canEdit && <Tab label={<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><AddIcon fontSize="small" /><span>Добавить опыт</span></Box>} />}
                        <Tab label={<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><HistoryIcon fontSize="small" /><span>История опыта</span></Box>} />
                    </Tabs>

                    {/* Вкладка: Добавить */}
                    {tab === 0 && (
                        <Box sx={{ py: 2 }}>
                            <Stack spacing={2}>
                                {success && (
                                    <Alert severity="success" onClose={() => setSuccess(false)}>
                                        Опыт успешно добавлен
                                    </Alert>
                                )}
                                {error && (
                                    <Alert severity="error" onClose={() => setError(null)}>
                                        {error}
                                    </Alert>
                                )}

                                <TextField
                                    label="Количество опыта"
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    inputProps={{ min: 1 }}
                                    fullWidth
                                    disabled={loading}
                                    autoFocus
                                />

                                <TextField
                                    label="Дата получения опыта"
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    fullWidth
                                    disabled={loading}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: getTodayString() }}
                                />

                                <FormControl fullWidth disabled={loading}>
                                    <InputLabel>Источник опыта</InputLabel>
                                    <Select
                                        value={sourceId}
                                        label="Источник опыта"
                                        onChange={(e: SelectChangeEvent) => {
                                            setSourceId(e.target.value);
                                            if (!sources.find(s => s.id === e.target.value)?.custom) {
                                                setCustomSource("");
                                            }
                                        }}
                                    >
                                        {sources.map(s => (
                                            <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedSource?.custom && (
                                    <TextField
                                        label="Источник опыта"
                                        value={customSource}
                                        onChange={(e) => setCustomSource(e.target.value)}
                                        placeholder="Например: победа в турнире, выполнение квеста..."
                                        fullWidth
                                        multiline
                                        rows={2}
                                        disabled={loading}
                                    />
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* Вкладка: История */}
                    {tab === (canEdit ? 1 : 0) && (
                        <Box sx={{ py: 2 }}>
                            {loading ? (
                                <Typography align="center" color="text.secondary">Загрузка...</Typography>
                            ) : history.length === 0 ? (
                                <Alert severity="info">У бойца ещё нет записей об опыте</Alert>
                            ) : (
                                <>
                                    {grouped.map(group => (
                                        <Box key={group.key} sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, pl: 0.5 }}>
                                                {group.label}
                                            </Typography>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell width={100}>Опыт</TableCell>
                                                            <TableCell>Источник</TableCell>
                                                            {canEdit && <TableCell width={48} />}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {group.items.map((item, i) => (
                                                            <TableRow key={i} hover>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={item.xp >= 0 ? `+${item.xp} XP` : `${item.xp} XP`}
                                                                        color={item.xp >= 0 ? "primary" : "error"}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">{item.description}</Typography>
                                                                </TableCell>
                                                                {canEdit && (
                                                                    <TableCell padding="none" align="center">
                                                                        {item.eventId && (
                                                                            <IconButton
                                                                                size="small"
                                                                                disabled={loading}
                                                                                onClick={() => handleDeleteXp(item.eventId!)}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        )}
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    ))}
                                    <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1, textAlign: "center" }}>
                                        <Typography variant="body2">
                                            Всего получено за всё время: <strong>{totalXp} XP</strong>
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
                <Button onClick={handleClose} disabled={loading}>Закрыть</Button>
                {canEdit && tab === 0 && (
                    <Button
                        onClick={handleAdd}
                        variant="contained"
                        disabled={loading}
                        startIcon={<AddIcon />}
                    >
                        {loading ? "Добавление..." : "Добавить опыт"}
                    </Button>
                )}
            </DialogActions>
        </UserDialog>
    );
}
